import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useChurch } from '../context/ChurchContext';
import { SkeletonStatCards } from '../components/common/Skeleton';
import useIsMobile from '../hooks/useIsMobile';
import usePermissions from '../hooks/usePermissions';

/* ── icônes inline ── */
function IcoCalendar() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
}
function IcoUsers() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
}
function IcoStar() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
}
function IcoBell() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
}
function IcoCheck() {
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
}
function IcoPin() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>;
}
function IcoClock() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
}
function IcoArrow() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>;
}
function IcoTrend() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;
}

const STATUS_LABELS = { planned: 'Planifiée', ongoing: 'En cours', completed: 'Terminée', cancelled: 'Annulée' };
const STATUS_COLORS = {
  planned:   { bg: '#f0f0f0', color: '#000000', dot: '#525252' },
  ongoing:   { bg: '#fafafa', color: '#262626', dot: '#404040' },
  completed: { bg: '#f5f5f5', color: '#404040', dot: '#737373' },
  cancelled: { bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444' },
};

const s = {
  page: { width: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  title: { fontSize: '1.5rem', fontWeight: 700, color: '#2C1F14', marginBottom: '2px' },
  subtitle: { fontSize: '0.92rem', color: '#6B5744' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' },
  statCard: (bg, border) => ({
    backgroundColor: bg, border: `1px solid ${border}`, borderRadius: '14px',
    padding: '20px', display: 'flex', alignItems: 'flex-start', gap: '14px',
    boxShadow: '0 1px 4px rgba(60,45,30,0.07)',
  }),
  statIcon: (bg) => ({
    width: '46px', height: '46px', borderRadius: '12px', backgroundColor: bg,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  }),
  statVal: (color) => ({ fontSize: '2rem', fontWeight: 800, color, lineHeight: 1.1 }),
  statLabel: (color) => ({ fontSize: '0.78rem', fontWeight: 600, color, marginBottom: '3px' }),
  statSub: { fontSize: '0.75rem', color: '#6B5744', marginTop: '1px' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' },
  card: { backgroundColor: '#FDFBF7', borderRadius: '14px', padding: '22px', border: '1px solid #E8DFD0', boxShadow: '0 1px 3px rgba(60,45,30,0.06)' },
  sectionTitle: { fontSize: '1rem', fontWeight: 700, color: '#2C1F14', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' },
  actRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #F0E8DC' },
  badge: { fontSize: '0.72rem', padding: '3px 9px', borderRadius: '999px', fontWeight: 600, whiteSpace: 'nowrap' },
  dot: (color) => ({ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color, flexShrink: 0 }),
  notifPanel: {
    backgroundColor: '#FDFBF7', borderRadius: '14px', padding: '22px',
    border: '1px solid #E8DFD0', boxShadow: '0 1px 3px rgba(60,45,30,0.06)',
    marginBottom: '20px',
  },
  notifItem: (unread) => ({
    display: 'flex', alignItems: 'flex-start', gap: '12px',
    padding: '12px', borderRadius: '10px', marginBottom: '8px',
    backgroundColor: unread ? '#f0f0f0' : '#F5F0E8',
    border: `1px solid ${unread ? '#d4d4d4' : '#E8DFD0'}`,
  }),
  notifIcon: (color) => ({
    width: '34px', height: '34px', borderRadius: '8px', backgroundColor: color,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  }),
  upcomingGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px', marginBottom: '20px' },
  upcomingCard: { backgroundColor: '#FDFBF7', borderRadius: '12px', border: '1px solid #E8DFD0', padding: '16px', boxShadow: '0 1px 3px rgba(60,45,30,0.05)' },
  metaRow: { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.8rem', color: '#6B5744', marginTop: '4px' },
  barRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
  barTrack: { flex: 1, backgroundColor: '#EDE5D8', borderRadius: '999px', height: '7px', overflow: 'hidden' },
  refreshBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    backgroundColor: '#F0EAE0', color: '#3D3028',
    border: '1px solid #D4C4B0', borderRadius: '8px',
    padding: '7px 14px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
  },
};

/* ── StatCard inline ── */
function StatCard({ title, value, Icon, colorBg, colorBorder, colorIcon, colorVal, subtitle }) {
  return (
    <div style={s.statCard(colorBg, colorBorder)}>
      <div style={s.statIcon('white')}>
        <span style={{ color: colorIcon }}><Icon /></span>
      </div>
      <div style={{ flex: 1 }}>
        <div style={s.statLabel(colorIcon)}>{title}</div>
        <div style={s.statVal(colorVal)}>{value ?? '—'}</div>
        {subtitle && <div style={s.statSub}>{subtitle}</div>}
      </div>
    </div>
  );
}

/* ── Notification item ── */
function NotifItem({ icon, iconBg, title, detail, time, unread }) {
  return (
    <div style={s.notifItem(unread)}>
      <div style={s.notifIcon(iconBg)}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.88rem', fontWeight: unread ? 700 : 500, color: '#1e293b' }}>{title}</div>
        <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>{detail}</div>
      </div>
      <div style={{ fontSize: '0.72rem', color: '#94a3b8', whiteSpace: 'nowrap', flexShrink: 0 }}>{time}</div>
    </div>
  );
}


const PERIODS = [
  { label: 'Ce mois', months: 0 },
  { label: '3 mois',  months: 3 },
  { label: '6 mois',  months: 6 },
  { label: '1 an',    months: 12 },
];

export default function Dashboard() {
  const { user } = useAuth();
  const { activeChurchId } = useChurch();
  const { canSeeActivites, canSeeFideles, canSeeFinance } = usePermissions();
  const navigate = useNavigate();
  const mob = useIsMobile();
  const [stats, setStats] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [upcomingActivities, setUpcomingActivities] = useState([]);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [topParticipants, setTopParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState(0); // months back; 0 = current month only
  const today = new Date();

  // Le tableau de bord général (Activités/Fidèles) n'a rien à montrer à un
  // utilisateur dont les seuls droits portent sur un module précis (ex:
  // Comptable, sans accès Activités/Fidèles) — le rediriger directement vers
  // ce module plutôt que de le laisser sur une page vide sans explication.
  // La ref évite un re-déclenchement en boucle (le remontage du composant en
  // StrictMode / la navigation elle-même peuvent re-fournir des dépendances
  // "nouvelles" à chaque passage sans elle).
  const redirectedRef = useRef(false);
  useEffect(() => {
    if (redirectedRef.current) return;
    if (!canSeeActivites && !canSeeFideles && canSeeFinance) {
      redirectedRef.current = true;
      navigate('/comptabilite', { replace: true });
    }
  }, [canSeeActivites, canSeeFideles, canSeeFinance, navigate]);

  const getPeriodDates = useCallback((months) => {
    const todayStr = format(today, 'yyyy-MM-dd');
    let startStr;
    if (months === 0) {
      startStr = format(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd');
    } else {
      const start = new Date(today);
      start.setMonth(start.getMonth() - months);
      startStr = format(start, 'yyyy-MM-dd');
    }
    return { startStr, todayStr };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = useCallback(async (silent = false, months = period) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const { startStr, todayStr } = getPeriodDates(months);

      // N'interroge que ce que l'utilisateur a le droit de voir — un compte
      // limité à un groupe sans accès Activités/Fidèles n'a aucune raison de
      // charger (ni d'afficher) ces statistiques sur le tableau de bord.
      const [statsRes, recentRes, upcomingRes, participantsRes, topRes] = await Promise.all([
        canSeeActivites ? api.get(`/attendance/statistics/?start_date=${startStr}&end_date=${todayStr}`) : Promise.resolve({ data: null }),
        canSeeActivites ? api.get('/activities/?ordering=-date,-start_time&page_size=5') : Promise.resolve({ data: [] }),
        canSeeActivites ? api.get(`/activities/?date_from=${todayStr}&status=planned&ordering=date,start_time&page_size=5`) : Promise.resolve({ data: [] }),
        canSeeFideles ? api.get('/participants/?page_size=1').catch(() => ({ data: { count: 0 } })) : Promise.resolve({ data: { count: 0 } }),
        canSeeFideles ? api.get(`/participants/?ordering=-attendance_count&page_size=5`).catch(() => ({ data: { results: [] } })) : Promise.resolve({ data: { results: [] } }),
      ]);

      setStats(statsRes.data);
      setRecentActivities(Array.isArray(recentRes.data) ? recentRes.data.slice(0, 5) : (recentRes.data.results || []).slice(0, 5));
      setUpcomingActivities(Array.isArray(upcomingRes.data) ? upcomingRes.data.slice(0, 4) : (upcomingRes.data.results || []).slice(0, 4));
      const totalPart = participantsRes.data?.count ?? (Array.isArray(participantsRes.data) ? participantsRes.data.length : 0);
      setTotalParticipants(totalPart);
      setTopParticipants((topRes.data.results || topRes.data || []).slice(0, 5));
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period, getPeriodDates, activeChurchId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePeriodChange = (months) => {
    setPeriod(months);
    fetchData(true, months);
  };

  const dateLabel = format(today, "EEEE d MMMM yyyy", { locale: fr });

  if (loading) {
    return (
      <div style={s.page}>
        <div style={{ ...s.header, marginBottom: '24px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ width: '200px', height: '24px', background: '#e2e8f0', borderRadius: '6px' }} />
            <div style={{ width: '140px', height: '16px', background: '#f1f5f9', borderRadius: '6px' }} />
          </div>
        </div>
        <SkeletonStatCards count={5} />
      </div>
    );
  }

  const totalActivities = stats?.total_activities || 0;
  const totalAttendance = stats?.total_attendance || 0;
  const nouveaux = stats?.status_breakdown?.nouveaux || 0;
  const trendData = stats?.trend || [];

  /* ── Notifications générées depuis les activités récentes ── */
  const notifications = [];
  recentActivities.forEach((act) => {
    if (act.total_attendance > 0) {
      notifications.push({
        id: `att-${act.id}`,
        type: 'attendance',
        title: `${act.total_attendance} présence${act.total_attendance > 1 ? 's' : ''} — ${act.name}`,
        detail: `${act.date}${act.start_time ? ' à ' + act.start_time.slice(0, 5) : ''} · ${act.location || ''}`,
        time: act.date,
        unread: act.status === 'ongoing',
      });
    }
  });
  upcomingActivities.forEach((act) => {
    notifications.push({
      id: `up-${act.id}`,
      type: 'upcoming',
      title: `Prochain événement : ${act.name}`,
      detail: `${act.date}${act.start_time ? ' à ' + act.start_time.slice(0, 5) : ''} · ${act.location || ''}`,
      time: act.date,
      unread: true,
    });
  });

  return (
    <div style={s.page}>
      {/* ── En-tête ── */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Bonjour, {user?.full_name || user?.username}</h1>
          <p style={s.subtitle}>{dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1)}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {/* Filtre période */}
          <div style={{ display: 'flex', border: '1px solid #E8DFD0', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#FDFBF7' }}>
            {PERIODS.map(p => (
              <button key={p.months} onClick={() => handlePeriodChange(p.months)}
                style={{ padding: '7px 13px', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700, borderLeft: p.months > 0 ? '1px solid #E8DFD0' : 'none', backgroundColor: period === p.months ? '#404040' : '#FDFBF7', color: period === p.months ? 'white' : '#3D3028', transition: 'all 0.15s' }}>
                {p.label}
              </button>
            ))}
          </div>
        <button
          style={s.refreshBtn}
          onClick={() => fetchData(true)}
          disabled={refreshing}
        >
          {refreshing ? (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
              <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          ) : (
            <IcoTrend />
          )}
          {refreshing ? 'Actualisation...' : 'Actualiser'}
        </button>
        </div>
      </div>

      {/* ── Stat cards ── */}
      {(canSeeActivites || canSeeFideles) && (
        <div style={{ ...s.statsGrid, gridTemplateColumns: mob ? '1fr 1fr' : 'repeat(auto-fill, minmax(220px, 1fr))' }}>
          {canSeeActivites && (
            <StatCard
              title={`Activités — ${PERIODS.find(p=>p.months===period)?.label || ''}`}
              value={totalActivities}
              Icon={IcoCalendar}
              colorBg="#f0f0f0" colorBorder="#d4d4d4" colorIcon="#404040" colorVal="#000000"
              subtitle="activités organisées"
            />
          )}
          {canSeeActivites && (
            <StatCard
              title={`Présences — ${PERIODS.find(p=>p.months===period)?.label || ''}`}
              value={totalAttendance}
              Icon={IcoUsers}
              colorBg="#fafafa" colorBorder="#d4d4d4" colorIcon="#525252" colorVal="#262626"
              subtitle="personnes enregistrées"
            />
          )}
          {canSeeFideles && (
            <StatCard
              title={`Nouveaux — ${PERIODS.find(p=>p.months===period)?.label || ''}`}
              value={nouveaux}
              Icon={IcoStar}
              colorBg="#FBF5EC" colorBorder="#E8D5B7" colorIcon="#C0883A" colorVal="#A06C28"
              subtitle="nouveaux visiteurs"
            />
          )}
          {canSeeFideles && (
            <StatCard
              title="Membres enregistrés"
              value={totalParticipants}
              Icon={IcoStar}
              colorBg="#F7F2EC" colorBorder="#D4C4B0" colorIcon="#8B7355" colorVal="#6B5744"
              subtitle="dans la base"
            />
          )}
          {canSeeActivites && (
            <StatCard
              title="Moy. présences/activité"
              value={totalActivities > 0 ? Math.round(totalAttendance / totalActivities) : '—'}
              Icon={IcoTrend}
              colorBg="#f0f0f0" colorBorder="#d4d4d4" colorIcon="#404040" colorVal="#000000"
              subtitle="ce mois"
            />
          )}
        </div>
      )}

      {/* ── Notifications ── */}
      {notifications.length > 0 && (
        <div style={s.notifPanel}>
          <div style={s.sectionTitle}>
            <span style={{ color: '#404040' }}><IcoBell /></span>
            Notifications
            {notifications.filter(n => n.unread).length > 0 && (
              <span style={{ backgroundColor: '#ef4444', color: 'white', fontSize: '0.7rem', fontWeight: 700, padding: '2px 7px', borderRadius: '999px' }}>
                {notifications.filter(n => n.unread).length}
              </span>
            )}
          </div>
          {notifications.slice(0, 6).map((n) => (
            <NotifItem
              key={n.id}
              unread={n.unread}
              title={n.title}
              detail={n.detail}
              time={n.time}
              iconBg={n.type === 'attendance' ? '#D4E8D4' : '#E8DFD0'}
              icon={
                n.type === 'attendance'
                  ? <span style={{ color: '#000000' }}><IcoUsers /></span>
                  : <span style={{ color: '#404040' }}><IcoCheck /></span>
              }
            />
          ))}
        </div>
      )}

      {/* ── 2 colonnes : activités récentes + répartition ── */}
      {canSeeActivites && (
        <div style={{ ...s.twoCol, gridTemplateColumns: mob ? '1fr' : '1fr 1fr' }}>
        {/* Activités récentes */}
        <div style={s.card}>
          <div style={s.sectionTitle}>
            <span style={{ color: '#404040' }}><IcoCalendar /></span>
            Activités récentes
          </div>
          {recentActivities.length === 0 ? (
            <p style={{ color: '#9C8878', fontSize: '0.9rem' }}>Aucune activité pour le moment.</p>
          ) : (
            recentActivities.map((act) => {
              const sc = STATUS_COLORS[act.status] || STATUS_COLORS.planned;
              return (
                <div key={act.id} style={s.actRow}>
                  <div style={s.dot(sc.dot)} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Link to={`/activites/${act.id}`} style={{ fontWeight: 600, color: '#2C1F14', fontSize: '0.9rem', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {act.name}
                    </Link>
                    <div style={{ fontSize: '0.78rem', color: '#6B5744', marginTop: '1px' }}>
                      {act.date}{act.start_time ? ' · ' + act.start_time.slice(0, 5) : ''}
                    </div>
                  </div>
                  <span style={{ ...s.badge, backgroundColor: sc.bg, color: sc.color }}>{STATUS_LABELS[act.status]}</span>
                  <span style={{ fontWeight: 700, color: '#404040', fontSize: '1rem', minWidth: '28px', textAlign: 'right' }}>{act.total_attendance}</span>
                </div>
              );
            })
          )}
          <div style={{ marginTop: '14px' }}>
            <Link to="/activites" style={{ color: '#404040', fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '5px' }}>
              Toutes les activités <IcoArrow />
            </Link>
          </div>
        </div>

        {/* Répartition */}
        <div style={s.card}>
          <div style={s.sectionTitle}>
            <span style={{ color: '#404040' }}><IcoUsers /></span>
            Répartition ce mois
          </div>
          {stats && totalAttendance > 0 ? (
            [
              { label: 'Hommes',     value: stats.gender_breakdown?.hommes || 0,         color: '#000000' },
              { label: 'Femmes',     value: stats.gender_breakdown?.femmes || 0,         color: '#525252' },
              { label: 'Enfants',    value: stats.gender_breakdown?.enfants || 0,        color: '#737373' },
              { label: 'Nouveaux',   value: stats.status_breakdown?.nouveaux || 0,       color: '#a3a3a3' },
              { label: 'Serviteurs', value: stats.status_breakdown?.serviteurs || 0,     color: '#404040' },
            ].map((item) => {
              const pct = Math.round((item.value / totalAttendance) * 100);
              return (
                <div key={item.label} style={s.barRow}>
                  <div style={{ width: '76px', fontSize: '0.82rem', color: '#374151', fontWeight: 500 }}>{item.label}</div>
                  <div style={s.barTrack}>
                    <div style={{ width: `${pct}%`, backgroundColor: item.color, height: '100%', borderRadius: '999px', transition: 'width 0.6s ease' }} />
                  </div>
                  <div style={{ width: '52px', textAlign: 'right', fontSize: '0.82rem', fontWeight: 700, color: item.color }}>{item.value} <span style={{ color: '#94a3b8', fontWeight: 400 }}>({pct}%)</span></div>
                </div>
              );
            })
          ) : (
            <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Aucune donnée disponible pour cette période.</p>
          )}
        </div>
      </div>
      )}

      {/* ── Top participants ── */}
      {canSeeFideles && topParticipants.length > 0 && (
        <div style={{ ...s.card, marginBottom: '20px' }}>
          <div style={s.sectionTitle}>
            <span style={{ color: '#404040' }}><IcoStar /></span>
            Top fidèles les plus assidus
          </div>
          {topParticipants.map((p, i) => {
            const max = topParticipants[0]?.attendance_count || 1;
            const pct = Math.round((p.attendance_count / max) * 100);
            const colors = ['#000000', '#404040', '#525252', '#737373', '#a3a3a3'];
            const initials = `${p.first_name?.[0] || ''}${p.last_name?.[0] || ''}`.toUpperCase();
            return (
              <Link key={p.id} to={`/participants/${p.id}`} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0', borderBottom: i < topParticipants.length - 1 ? '1px solid #F0E8DC' : 'none' }}>
                <div style={{ width: '24px', textAlign: 'center', fontWeight: 800, fontSize: '0.9rem', color: colors[i] || '#6B5744', flexShrink: 0 }}>#{i + 1}</div>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem', color: '#404040' }}>
                  {p.photo_url ? <img src={p.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#2C1F14', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.full_name || `${p.first_name} ${p.last_name}`}</div>
                  <div style={s.barTrack}>
                    <div style={{ width: `${pct}%`, backgroundColor: colors[i] || '#404040', height: '100%', borderRadius: '999px', transition: 'width 0.6s ease' }} />
                  </div>
                </div>
                <div style={{ fontWeight: 800, color: colors[i] || '#404040', fontSize: '1rem', minWidth: '28px', textAlign: 'right', flexShrink: 0 }}>{p.attendance_count}</div>
              </Link>
            );
          })}
        </div>
      )}

      {/* ── Prochains événements ── */}
      {upcomingActivities.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ ...s.sectionTitle, marginBottom: '14px' }}>
            <span style={{ color: '#1e40af' }}><IcoClock /></span>
            Prochains événements
          </div>
          <div style={{ ...s.upcomingGrid, gridTemplateColumns: mob ? '1fr 1fr' : 'repeat(auto-fill, minmax(220px, 1fr))' }}>
            {upcomingActivities.map((act) => {
              const sc = STATUS_COLORS[act.status] || STATUS_COLORS.planned;
              return (
                <Link key={act.id} to={`/activites/${act.id}`} style={{ textDecoration: 'none' }}>
                  <div style={s.upcomingCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', flex: 1, marginRight: '8px', lineHeight: 1.3 }}>{act.name}</div>
                      <span style={{ ...s.badge, backgroundColor: sc.bg, color: sc.color, flexShrink: 0 }}>{STATUS_LABELS[act.status]}</span>
                    </div>
                    <div style={s.metaRow}><IcoClock />{act.date}{act.start_time ? ' · ' + act.start_time.slice(0, 5) : ''}</div>
                    {act.location && <div style={s.metaRow}><IcoPin />{act.location}</div>}
                    <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Inscriptions :</span>
                      <span style={{ fontWeight: 700, color: '#1e40af', fontSize: '0.9rem' }}>{act.total_attendance}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Graphique tendance ── */}
      {trendData.length > 0 && (
        <div style={s.card}>
          <div style={s.sectionTitle}>
            <span style={{ color: '#1e40af' }}><IcoTrend /></span>
            Évolution des présences
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={trendData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => v.length > 14 ? v.slice(0, 13) + '…' : v} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
              <Tooltip formatter={(v) => [`${v} présences`, 'Total']} labelStyle={{ fontWeight: 600, color: '#1e293b' }} />
              <Bar dataKey="count" fill="#1e40af" radius={[5, 5, 0, 0]} name="Présences" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
